import { deepSubsetEqual } from '@/lib/DeepCompare';
import { getSortingGroup } from '@/lib/Filter';
import { useAppDispatch, useAppSelector } from '@/lib/hooks';
import { defaultFilterOptions, setDocumentSearchType } from '@/lib/state/slices/ProjectStateSlice';
import {
  IconButton,
  Popover,
  Select,
  Stack,
  Portal,
  createListCollection,
  StatusRoot,
  StatusIndicator,
} from '@chakra-ui/react';
import { ValueChangeDetails } from '@chakra-ui/react/dist/types/components/accordion/namespace';
import { useEffect, useRef, useState } from 'react';
import { HelpCircle, Filter as FilterIcon } from 'react-feather';

import { Tooltip } from '../ui/tooltip';
import FilterSelector from './FilterSelector';
import { useSystemTheme } from '@/lib/ThemeSwitcher';

export default function SearchOptions() {

  const documentSearchType = useAppSelector((state) => state.viewReducer.documents.search.type);
  const filter = useAppSelector((state) => state.viewReducer.documents.filter);

  const [openFilter, setOpenFilter] = useState(false);
  const [isFilterActive, setIsFilterActive] = useState(true);
  const { system } = useSystemTheme();
  
  
  const dispatch = useAppDispatch();
  const initialFocusRef = useRef(null);

  const filteredSortingOptions = getSortingGroup(documentSearchType);
  
  const searchOptions = createListCollection({
    items: filteredSortingOptions.map((e) => {
      return { label: e, value: e };
    }),
  });
  
  useEffect(() => {
    setIsFilterActive(!deepSubsetEqual(defaultFilterOptions, filter));
  }, [JSON.stringify(filter)]);
  
  return (
    <Stack width="100%" direction="row">
      <Tooltip content="The filtering only works on the exact search right now. Updates for the graph and contextual search will follow soon.">
        <IconButton
          size="md"
          as={'div'}
          borderRadius="0.375rem"
          aria-label="search_help"
          aria-hidden={false}
          background="cardDefault"
          color="secondaryText"
          border="none"
          _focus={{ bg: 'cardDefault' }}
          _hover={{ bg: 'cardHover', transition: 'background-color 0.2s' }}
          _active={{ bg: 'cardActive', transition: 'background-color 0.2s' }}
          cursor="help"
        >
          <HelpCircle />
        </IconButton>
      </Tooltip>

      <Popover.Root
        initialFocusRef={initialFocusRef}
        open={openFilter}
        onOpenChange={(e: any) => setOpenFilter(e.open)}
        placement="bottom-start"
        offset={[0, 0]}
      >
        <Popover.Trigger>
          <StatusRoot position={'relative'}>
            {isFilterActive && (
              <Tooltip content="You have filters active which might limit the results you are seeing">
                <StatusIndicator
                  style={{
                    position: 'absolute',
                    top: -1,
                    right: -1,
                    background: system.tokens.getVar('colors.accentBlue'),
                    zIndex: 2,
                  }}
                />
              </Tooltip>
            )}
            <IconButton
              size="md"
              as={'div'}
              borderRadius="0.375rem"
              aria-label="search_filter"
              aria-hidden={false}
              background="cardDefault"
              color="secondaryText"
              border="none"
              _focus={{ bg: 'cardDefault' }}
              _hover={{ bg: 'cardHover', transition: 'background-color 0.2s' }}
              _active={{ bg: 'cardActive', transition: 'background-color 0.2s' }}
              onClick={() => setOpenFilter(true)}
            >
              <FilterIcon />
            </IconButton>
          </StatusRoot>
        </Popover.Trigger>

        <Portal>
          <Popover.Positioner>
            <Popover.Content
              backgroundColor="cardDefault"
              borderRadius="0.375rem"
              width="fit-content"
              padding={0}
            >
              <FilterSelector onClose={() => setOpenFilter(false)} />
            </Popover.Content>
          </Popover.Positioner>
        </Portal>
      </Popover.Root>

      <Select.Root
        size="md"
        width="100%"
        borderRadius="0.375rem"
        background="cardDefault"
        color="secondaryText"
        border="none"
        _focus={{ bg: 'cardDefault' }}
        _hover={{ bg: 'cardHover', transition: 'background-color 0.2s' }}
        _active={{ bg: 'cardActive', transition: 'background-color 0.2s' }}
        defaultValue={[documentSearchType]}
        value={[documentSearchType]}
        onValueChange={(
          details: ValueChangeDetails<{ items: Array<string>; value: Array<string> }>,
        ) => {
          dispatch(setDocumentSearchType(details.value[0]));
        }}
        collection={searchOptions}
      >
        <Select.HiddenSelect />
        <Select.Control>
          <Select.Trigger
            width="100%"
            style={{
              border: 'none',
              cursor: 'pointer',
            }}
          >
            <Select.ValueText />
          </Select.Trigger>
          <Select.IndicatorGroup>
            <Select.Indicator color="secondaryText" />
          </Select.IndicatorGroup>
        </Select.Control>

        <Portal>
          <Select.Positioner>
            <Select.Content backgroundColor="cardDefault">
              {searchOptions.items.map((searchOption: { label: string; value: DocumentType }) => {
                return (
                  <Select.Item
                    color="secondaryText"
                    css={{
                      '&[data-state="checked"]': {
                        background: 'cardActive',
                      },
                    }}
                    _focus={{ bg: 'cardDefault' }}
                    _hover={{ bg: 'cardHover', transition: 'background-color 0.2s' }}
                    _active={{ bg: 'cardActive', transition: 'background-color 0.2s' }}
                    key={searchOption.value}
                    value={searchOption.value}
                    item={searchOption}
                  >
                    {searchOption.label}
                    <Select.ItemIndicator />
                  </Select.Item>
                );
              })}
            </Select.Content>
          </Select.Positioner>
        </Portal>
      </Select.Root>
    </Stack>
  );
}
